import { useEffect, useRef } from 'react';

const SCROLL_DISTANCE = 420;

const ScrollSectionBackground = ({ edge }: { edge: 'top' | 'bottom' }) => {
  const backgroundRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const background = backgroundRef.current;
    if (!background) return;

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let frame: number | undefined;
    let previousInset = '';

    const updateInset = () => {
      frame = undefined;
      if (reducedMotion.matches) return;

      const distance = edge === 'top'
        ? window.scrollY
        : background.getBoundingClientRect().bottom - window.innerHeight;
      const progress = Math.min(1, Math.max(0, distance / SCROLL_DISTANCE));
      // Ease into and out of the inset so it starts and stops without a snap.
      const inset = (progress * progress * (3 - 2 * progress)).toFixed(4);
      if (inset !== previousInset) {
        background.style.setProperty('--section-inset', inset);
        previousInset = inset;
      }
    };

    const scheduleUpdate = () => {
      if (frame === undefined && !reducedMotion.matches) {
        frame = window.requestAnimationFrame(updateInset);
      }
    };

    updateInset();
    window.addEventListener('scroll', scheduleUpdate, { passive: true });
    window.addEventListener('resize', scheduleUpdate);
    reducedMotion.addEventListener('change', scheduleUpdate);

    return () => {
      if (frame !== undefined) window.cancelAnimationFrame(frame);
      window.removeEventListener('scroll', scheduleUpdate);
      window.removeEventListener('resize', scheduleUpdate);
      reducedMotion.removeEventListener('change', scheduleUpdate);
    };
  }, [edge]);

  return <div ref={backgroundRef} className={`scroll-section-background scroll-section-background--${edge}`} aria-hidden="true" />;
};

export default ScrollSectionBackground;
